'use client';

import React from 'react';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';

import { faBars, faCaretDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Flex, Menu, MenuDropdown, MenuItem, MenuTarget } from '@mantine/core';

import { useAppContext } from '@context/AppContext';

import classes from './NavMenu.module.css';

export default function NavMenu() {
    const [menu] = useAppContext('menu');
    const pathname = usePathname();
    const t = useTranslations();
    const viewportRef = React.useRef(null);
    const overflowButtonRef = React.useRef(null);
    const measureItemRefs = React.useRef([]);
    const [visibleCount, setVisibleCount] = React.useState(menu?.length || 0);

    const isHomeEntry = (entry) => entry.href === '/' || entry.label === 'home' || entry.id === 'home';

    const flatEntries = React.useMemo(() => menu
        .flatMap((m) => {
            const topLevelEntry = m.href
                ? [{ id: String(m.id), href: m.href, label: m.label || m.id }]
                : [];
            const childrenEntries = Array.isArray(m.children)
                ? m.children.map((child) => ({
                    id: `${m.id}-${child.id}`,
                    href: child.href,
                    label: child.label || child.id,
                }))
                : [];

            return [...topLevelEntry, ...childrenEntries];
        })
        .filter((entry) => entry.href && !isHomeEntry(entry)), [menu]);

    const uniqueEntries = React.useMemo(
        () => Array.from(new Map(flatEntries.map((entry) => [entry.href, entry])).values()),
        [flatEntries],
    );

    React.useEffect(() => {
        setVisibleCount(uniqueEntries.length);
    }, [uniqueEntries.length]);

    React.useLayoutEffect(() => {
        const GAP_PX = 8;

        const updateVisibleCount = () => {
            if (!viewportRef.current) {
                return;
            }

            const containerWidth = viewportRef.current.clientWidth;
            const itemWidths = uniqueEntries.map((_, index) => {
                const node = measureItemRefs.current[index];
                return node ? node.offsetWidth : 0;
            });

            if (!itemWidths.length) {
                setVisibleCount(0);
                return;
            }

            let fitAllWidth = 0;
            itemWidths.forEach((width, index) => {
                fitAllWidth += width;
                if (index > 0) {
                    fitAllWidth += GAP_PX;
                }
            });

            if (fitAllWidth <= containerWidth) {
                setVisibleCount(itemWidths.length);
                return;
            }

            const overflowButtonWidth = overflowButtonRef.current?.offsetWidth || 36;
            const availableWidth = Math.max(containerWidth - overflowButtonWidth - GAP_PX, 0);

            let count = 0;
            let usedWidth = 0;
            itemWidths.forEach((width, index) => {
                const nextUsedWidth = usedWidth + width + (index > 0 ? GAP_PX : 0);
                if (nextUsedWidth <= availableWidth) {
                    usedWidth = nextUsedWidth;
                    count += 1;
                }
            });

            setVisibleCount(count);
        };

        updateVisibleCount();

        const resizeObserver = new ResizeObserver(() => {
            updateVisibleCount();
        });

        resizeObserver.observe(viewportRef.current);

        return () => {
            resizeObserver.disconnect();
        };
    }, [uniqueEntries]);

    const visibleEntries = uniqueEntries.slice(0, visibleCount);
    const overflowEntries = uniqueEntries.slice(visibleCount);
    const fullMenuOverflow = overflowEntries.length === uniqueEntries.length;

    const visibleItems = visibleEntries.map((entry) => {
        const isActive = pathname === entry.href;

        return (
            <Link
                key={entry.id}
                href={entry.href}
                className={`${classes.link} ${isActive ? classes.active : ''}`}
            >
                {t(entry.label)}
            </Link>
        );
    });

    const overflowItems = overflowEntries.map((entry) => {
        const isActive = pathname === entry.href;

        return (
            <MenuItem
                key={`overflow-${entry.id}`}
                component={Link}
                href={entry.href}
                className={`${classes.overflowItem} ${isActive ? classes.overflowItemActive : ''}`}
            >
                {t(entry.label)}
            </MenuItem>
        );
    });

    return (
        <>
            <div className={classes.measureContainer} aria-hidden="true">
                {uniqueEntries.map((entry, index) => (
                    <span
                        key={`measure-${entry.id}`}
                        ref={(node) => {
                            measureItemRefs.current[index] = node;
                        }}
                        className={classes.link}
                    >
                        {t(entry.label)}
                    </span>
                ))}
                <span ref={overflowButtonRef} className={classes.overflowButton}>...</span>
            </div>
            <div className={classes.navViewport} ref={viewportRef}>
                <Flex align="center" gap="xs" className={classes.navRoot}>
                    {visibleItems}
                    {overflowEntries.length > 0 && (
                        <Menu
                            position="bottom-start"
                            offset={6}
                            shadow="md"
                            withinPortal
                        >
                            <MenuTarget>
                                <button type="button" className={classes.overflowButton} aria-label="Plus de liens">
                                    <FontAwesomeIcon icon={fullMenuOverflow ? faBars : faCaretDown} />
                                </button>
                            </MenuTarget>
                            <MenuDropdown className={classes.overflowDropdown}>
                                {overflowItems}
                            </MenuDropdown>
                        </Menu>
                    )}
                </Flex>
            </div>
        </>
    );
}
